import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ClientSessionService } from '@brighthub/uisession';
import { Button } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import {
  ClientProfileKind,
  ClientProfileSection,
  ClientProfileItem,
} from '../client-profile.model';
import {
  formatClientProfilePhoneDisplay,
  inferClientProfileValueDisplayKind,
  toFiniteNumber,
  type ClientProfileValueDisplayKind,
} from '../client-profile-item-display';
import {
  expandClientProfileSectionToBlocks,
  type ClientProfileSectionBlock,
} from '../client-profile-section-expand';
import { ClientProfileSectionsService } from '../client-profile-sections.service';
import { ClientProfileEditService } from '../client-profile-edit.service';
import { GenericResponseCode } from '@brighthub/api';

type ItemWithDisplay = ClientProfileItem & {
  displayValue: string | number | null;
  valueDisplayKind: ClientProfileValueDisplayKind;
  currencyAmount: number | null;
  /** Set when {@link valueDisplayKind} is `phone`. */
  formattedPhone: string | null;
};

/** Leaf block under a top-level section (flat rows or one nested group). */
interface SubsectionWithDisplayValues {
  blockId: string;
  subsectionTitle: string | null;
  items: ItemWithDisplay[];
  hasEditableItems: boolean;
  asSectionForEdit: ClientProfileSection;
}

/** Top-level section with expanded subsections for template and edit. */
interface SectionWithDisplayValues {
  id?: string;
  title: string;
  /** Stable, HTML-safe id for section heading and aria-labelledby (not raw title/id). */
  sectionDomId: string;
  sectionIcon: string;
  subsections: SubsectionWithDisplayValues[];
}

@Component({
  selector: 'bn-client-profile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, Button],
  providers: [DialogService, ClientProfileEditService],
  templateUrl: './client-profile.component.html',
  styleUrl: './client-profile.component.scss',
})
export class ClientProfileComponent {
  private _session = inject(ClientSessionService);
  private _profileSections = inject(ClientProfileSectionsService);
  readonly editService = inject(ClientProfileEditService);

  /**
   * Optional GUID override (e.g. household member dialog). When unset, uses the
   * active session subject GUID (Profile tab).
   */
  readonly guid = input<string | null>(null);
  /**
   * Subject kind for future write rules (employee vs household member).
   * Does not change read layout today.
   */
  readonly profileKind = input<ClientProfileKind>('employee');
  /**
   * When true, show edit pencils for API-editable fields. Defaults to false —
   * editing is opt-in until product enables it (e.g. employees only).
   */
  readonly allowEdit = input(false);

  /** Sections from API; empty when no client or loading/error */
  readonly sections = signal<ClientProfileSection[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /** Effective GUID: input override, else session. */
  private _resolvedGuid = computed(() => {
    const override = this.guid()?.trim();
    if (override) {
      return override;
    }
    const session = this._session.selectedSession();
    return session?.subjectData?.guid ?? null;
  });

  /** Sections with precomputed display values and hasEditableItems so the template never calls methods. */
  readonly sectionsWithDisplayValues = computed<SectionWithDisplayValues[]>(() => {
    const s = this.sections();
    const edits = this.editService.editValues();
    const canEdit = this.allowEdit();
    return s.map((section) => {
      const blocks = expandClientProfileSectionToBlocks(section);
      const subsections = blocks
        .map((block: ClientProfileSectionBlock) => {
          const items = block.items
            .map((item) => {
              const key = this._itemEditKey(block.id, item);
              const displayValue =
                edits[key] !== undefined ? edits[key] : item.value;
              let valueDisplayKind = inferClientProfileValueDisplayKind(
                item,
                displayValue
              );
              let currencyAmount: number | null = null;
              let formattedPhone: string | null = null;
              if (valueDisplayKind === 'currency') {
                currencyAmount = toFiniteNumber(displayValue);
                if (currencyAmount === null) {
                  valueDisplayKind = 'text';
                }
              }
              if (valueDisplayKind === 'phone' && displayValue != null) {
                formattedPhone =
                  formatClientProfilePhoneDisplay(displayValue) ??
                  String(displayValue);
              }
              return {
                ...item,
                displayValue,
                valueDisplayKind,
                currencyAmount,
                formattedPhone,
              };
            })
            .filter((row) => row.displayValue != null);
          const hasEditableItems =
            canEdit && block.items.some((i) => i.editable);
          const asSectionForEdit: ClientProfileSection = {
            id: block.id,
            title: block.subsectionTitle ?? section.title,
            items: block.items,
          };
          return {
            blockId: block.id,
            subsectionTitle: block.subsectionTitle,
            items,
            hasEditableItems,
            asSectionForEdit,
          };
        })
        .filter((sub) => sub.items.length > 0);
      const sectionIcon = this._sectionIcon(section);
      return {
        id: section.id,
        title: section.title,
        sectionIcon,
        subsections,
      };
    })
      .filter((section) => section.subsections.length > 0)
      .map((section, index) => ({
        ...section,
        sectionDomId: this._sectionDomId(section.id, section.title, index),
      }));
  });

  constructor() {
    effect((onCleanup) => {
      const guid = this._resolvedGuid();
      // Touch profileKind so future write/policy effects can depend on it without unused-input lint.
      void this.profileKind();

      this.error.set(null);

      if (!guid) {
        this.sections.set([]);
        this.loading.set(false);
        return;
      }

      const cached = this._profileSections.peekSections(guid);
      if (cached) {
        this.sections.set(cached);
        this.loading.set(false);
      } else {
        this.sections.set([]);
        this.loading.set(true);
      }

      const sub = this._profileSections.getProfileSections(guid).subscribe({
        next: (res) => {
          this.loading.set(false);

          if (
            res.result.code === GenericResponseCode.SERVICE_OK &&
            res.payload &&
            Array.isArray(res.payload.sections)
          ) {
            this.sections.set(res.payload.sections);
            this.error.set(null);
          } else {
            this.sections.set([]);
            this.error.set(res.result.message || 'Failed to load profile');
          }
        },
        error: (err) => {
          this.loading.set(false);
          this.sections.set([]);
          this.error.set(err?.message ?? 'Failed to load profile');
        },
      });

      // Cancels the previous request when guid changes (or component destroyed)
      onCleanup(() => sub.unsubscribe());
    });
  }

  private _itemEditKey(sectionId: string, item: ClientProfileItem): string {
    return `${sectionId ?? 'section'}-${item.key ?? item.label}`;
  }

  /**
   * HTML id / aria-labelledby target: slugify API id or title, suffix index for uniqueness.
   */
  private _sectionDomId(
    id: string | undefined,
    title: string,
    index: number
  ): string {
    const raw = (id?.trim() || title).trim();
    const slug = this._slugifyHtmlIdFragment(raw);
    return `profile-section-${slug}-${index}`;
  }

  /** Safe fragment for id: letters, digits, hyphens; whitespace → hyphen; drop other chars. */
  private _slugifyHtmlIdFragment(s: string): string {
    const slug = s
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]+/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return slug || 'section';
  }

  /** PrimeIcon class for section header (e.g. pi-user, pi-briefcase). Improves scanability. */
  private _sectionIcon(section: ClientProfileSection): string {
    const id = (section.id ?? section.title ?? '').toLowerCase();
    if (id === 'person' || id.includes('personal')) return 'pi-user';
    if (id.includes('employment')) return 'pi-briefcase';
    if (id.includes('location')) return 'pi-map-marker';
    if (id.includes('contact')) return 'pi-phone';
    if (id.includes('benefits')) return 'pi-wallet';
    if (id.includes('household')) return 'pi-home';
    return 'pi-folder';
  }
}
